import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag } from "lucide-react";
import heroImage from "@/assets/hero-main.jpg";
import HeroSoundtrack from "@/components/home/HeroSoundtrack";

const HeroEditorial = () => {
  return (
    <section className="relative min-h-[92vh] md:min-h-[96vh] flex items-end overflow-hidden bg-[hsl(150_35%_8%)]">
      {/* Hero image with slow cinematic zoom — focal point shifted right on mobile so face sits away from text */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={heroImage}
          alt="Caribbean botanical heritage — Sugar Town Organics legacy house"
          className="absolute inset-0 w-full h-full object-cover object-[78%_center] md:object-center opacity-95 will-change-transform animate-[hero-zoom_22s_ease-out_forwards]"
          width={1920}
          height={1080}
          fetchPriority="high"
        />
      </div>

      {/* Inline keyframes for slow zoom (kept local to avoid touching tailwind config) */}
      <style>{`
        @keyframes hero-zoom {
          from { transform: scale(1.08); }
          to { transform: scale(1.0); }
        }
      `}</style>

      {/* Original cinematic overlay — restores text legibility across the full hero */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, hsl(150 40% 4% / 0.45) 0%, hsl(150 40% 4% / 0.55) 50%, hsl(150 40% 4% / 0.9) 100%)",
        }}
      />

      <div className="relative z-10 container mx-auto px-6 pb-24 md:pb-32">
        <div className="max-w-3xl animate-fade-in-up">
          <p
            className="font-sans text-[11px] md:text-xs tracking-[0.42em] uppercase text-accent mb-8 md:mb-7"
            style={{ textShadow: "0 1px 14px hsl(150 35% 6% / 0.8)" }}
          >
            A Caribbean Legacy House · St. Kitts &amp; Nevis
          </p>
          <h1
            className="text-[2.75rem] leading-[1.02] md:text-7xl lg:text-[5.5rem] font-serif font-bold text-white md:leading-[0.98] mb-8 md:mb-9 tracking-tight"
            style={{ textShadow: "0 2px 28px hsl(150 35% 6% / 0.7), 0 1px 2px hsl(150 35% 6% / 0.6)" }}
          >
            Rooted Memory.
            <br />
            <span
              className="italic font-normal text-accent"
              style={{ textShadow: "0 2px 24px hsl(150 35% 6% / 0.8)" }}
            >
              Living Future.
            </span>
          </h1>
          <p
            className="text-[15px] md:text-lg text-white font-sans leading-relaxed mb-10 md:mb-12 max-w-[20rem] sm:max-w-md md:max-w-2xl"
            style={{ textShadow: "0 2px 18px hsl(150 35% 4% / 0.95), 0 1px 4px hsl(150 35% 4% / 0.9)" }}
          >
            A Caribbean Legacy House rooted in the botanical wisdom, cultural
            memory and living traditions of St. Kitts &amp; Nevis — crafting
            beauty, wellness, food, publishing and immersive experiences for
            modern Caribbean living.
          </p>

          {/* Signature soundtrack — discovered, not advertised */}
          <HeroSoundtrack />

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#begin-journey"
              className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-9 py-4 rounded-sm font-sans font-semibold text-[11px] uppercase tracking-[0.28em] hover:opacity-90 transition-opacity shadow-[0_14px_48px_-10px_hsl(var(--accent)/0.6)]"
            >
              Enter the Method <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/shop"
              className="inline-flex items-center justify-center gap-2 border border-[hsl(40_33%_94%)]/45 text-[hsl(40_33%_96%)] px-9 py-4 rounded-sm font-sans font-semibold text-[11px] uppercase tracking-[0.28em] hover:border-accent hover:text-accent transition-colors backdrop-blur-sm bg-[hsl(150_35%_8%)]/30"
            >
              <ShoppingBag className="w-4 h-4" /> Shop the Rituals
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom cinematic fade into next section */}
      <div
        aria-hidden
        className="absolute bottom-0 inset-x-0 h-32 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, hsl(var(--background)) 100%)",
        }}
      />
      {/* Gold thread */}
      <div
        aria-hidden
        className="absolute bottom-0 inset-x-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, hsl(40 55% 55% / 0.7) 50%, transparent 100%)",
        }}
      />
    </section>
  );
};

export default HeroEditorial;
